import { queryKeys } from "@/constants/queryKeyFactory";
import { useMutation, useQuery } from "@/hooks/api";
import {
  ApiResponse,
  PaginationParams,
  QueryOptions,
} from "@/interfaces/api.interface";
import { ICompany, ICompanyWithUsers } from "@/interfaces/company.interface";
import { queryClient } from "@/lib/queryClient";
import { companySvc } from "@/services/company.service";

export function useCompanies(
  params: PaginationParams = {},
  options: QueryOptions = {}
) {
  return useQuery<ApiResponse<ICompany[]>>(
    queryKeys.company.list(params),
    () =>
      companySvc.get<ICompany>({
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
        sort: params.sort,
        filter: params.filter,
        searchFilters: params.searchFilters,
      }),
    options
  );
}

export function useCompaniesWithUsers(
  projectId: string,
  params: PaginationParams = {},
  options: QueryOptions = {}
) {
  return useQuery<ApiResponse<ICompanyWithUsers[]>>(
    queryKeys.company.users(projectId),
    () => companySvc.getWithUsers(projectId),
    options
  );
}

export function useAllCompanies(
  params: PaginationParams = {},
  options: QueryOptions = {}
) {
  return useQuery<ApiResponse<ICompany[]>>(
    queryKeys.company.all({ ...params }),
    () => companySvc.getAll<ICompany>(params),
    options
  );
}

export function useCreateCompany(options: QueryOptions = {}) {
  return useMutation<ApiResponse<ICompany>, ICompany>(
    (data: ICompany) => companySvc.create<ICompany>(data),
    {
      ...options,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.company.root,
        });
      },
    }
  );
}

export function useUpdateCompany(options: QueryOptions = {}) {
  return useMutation<ApiResponse<ICompany>, { id: string; data: ICompany }>(
    ({ id, data }) => companySvc.update<ICompany>(id, data),
    {
      ...options,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.company.root,
          refetchType: "active",
        });
      },
    }
  );
}
